import React, { useState } from 'react';
import DocumentsTracking from './DocumentsTracking';
import EulaAgreement from './EulaAgreement';
import TelehealthConsent from './policies/TelehealthConsent';
import PracticePolicies from './policies/PracticePolicies';
import { DocumentTrackingItem } from '../../types';

interface DocumentManagerProps {
  clientId: string;
  onDocumentComplete?: (documentId: string, signature: string) => void;
}

const DocumentManager: React.FC<DocumentManagerProps> = ({
  clientId,
  onDocumentComplete
}) => {
  const [currentView, setCurrentView] = useState<'tracking' | 'document'>('tracking');
  const [selectedDocument, setSelectedDocument] = useState<string | null>(null);
  const [completedDocuments, setCompletedDocuments] = useState<Set<string>>(new Set());

  const handleDocumentSelect = (documentId: string) => {
    setSelectedDocument(documentId);
    setCurrentView('document');
  };

  const handleDocumentAccept = (documentId: string, signature: string) => {
    // Mark document as completed
    setCompletedDocuments(prev => {
      const newSet = new Set(prev);
      newSet.add(documentId);
      return newSet;
    });

    // Notify parent component
    if (onDocumentComplete) {
      onDocumentComplete(documentId, signature);
    }

    // Return to tracking view
    setCurrentView('tracking');
    setSelectedDocument(null);
  };

  const handleBackToTracking = () => {
    setCurrentView('tracking');
    setSelectedDocument(null);
  };

  const renderDocumentComponent = () => {
    if (!selectedDocument) return null;

    const handleAccept = (signature: string) => {
      handleDocumentAccept(selectedDocument, signature);
    };

    switch (selectedDocument) {
      case 'eula-agreement':
        return <EulaAgreement onAccept={handleAccept} />;
      case 'telehealth-consent':
        return <TelehealthConsent onAccept={handleAccept} />;
      case 'practice-policies':
        return <PracticePolicies onAccept={handleAccept} />;
      case 'credit-card-auth':
        return (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <h2>Credit Card Authorization</h2>
            <p>Credit Card Authorization component would be implemented here</p>
            <button onClick={() => handleAccept('mock-signature')}>
              Mock Accept
            </button>
          </div>
        );
      case 'informed-consent-psychotherapy':
        return (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <h2>Informed Consent for Psychotherapy</h2>
            <p>Psychotherapy consent component would be implemented here</p>
            <button onClick={() => handleAccept('mock-signature')}>
              Mock Accept
            </button>
          </div>
        );
      case 'privacy-practices':
        return (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <h2>Notice of Privacy Practices</h2>
            <p>Privacy practices component would be implemented here</p>
            <button onClick={() => handleAccept('mock-signature')}>
              Mock Accept
            </button>
          </div>
        );
      case 'demographics-form':
        return (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <h2>Demographics Form</h2>
            <p>Demographics form component would be implemented here</p>
            <button onClick={() => handleAccept('mock-signature')}>
              Mock Complete
            </button>
          </div>
        );
      case 'gad-7':
        return (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <h2>GAD-7 Assessment</h2>
            <p>GAD-7 assessment component would be implemented here</p>
            <button onClick={() => handleAccept('mock-signature')}>
              Mock Complete
            </button>
          </div>
        );
      case 'phq-9':
        return (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <h2>PHQ-9 Assessment</h2>
            <p>PHQ-9 assessment component would be implemented here</p>
            <button onClick={() => handleAccept('mock-signature')}>
              Mock Complete
            </button>
          </div>
        );
      case 'intake-questionnaire':
        return (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <h2>Standard Intake Questionnaire</h2>
            <p>Intake questionnaire component would be implemented here</p>
            <button onClick={() => handleAccept('mock-signature')}>
              Mock Complete
            </button>
          </div>
        );
      default:
        return (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <h2>Document Not Found</h2>
            <p>The requested document could not be found.</p>
            <button onClick={handleBackToTracking}>
              Back to Documents
            </button>
          </div>
        );
    }
  };

  if (currentView === 'document') {
    return (
      <div>
        {/* Back Button */}
        <div style={{ margin: '1rem 0' }}>
          <button
            onClick={handleBackToTracking}
            style={{
              background: '#374151',
              color: '#f9fafb',
              border: '1px solid #6b7280',
              borderRadius: '6px',
              padding: '0.5rem 1rem',
              fontSize: '0.875rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            ← Back to Documents
          </button>
        </div>

        {renderDocumentComponent()}
      </div>
    );
  }

  return (
    <DocumentsTracking
      clientId={clientId}
      onDocumentSelect={handleDocumentSelect}
    />
  );
};

export default DocumentManager;